/**
 * OfferCellValue
 *
 * Field-key dispatch for one cell inside `OffersCompareGrid` or `OfferListRow`.
 * Numeric cells render with `fontVariantNumeric: 'tabular-nums'` (CSS `tnum`
 * feature) so columns line up across offers. The values themselves are NEVER
 * passed through `t()` — only the unit suffix ("/mo", "fixed", "years") goes
 * through i18n. This preserves digit alignment across translations.
 */

import { Box, GlassChip, Typography, useI18n } from '@ops-matrix/design-system-ui';
import type { ReactElement } from 'react';
import type { OfferRecord } from './types';

/** Sequence of compare-grid rows. `key` drives both the row label AND the cell render. */
export type CompareRowKey =
  | 'lender'
  | 'amount'
  | 'rate'
  | 'term'
  | 'monthly'
  | 'highlights'
  | 'cautions'
  | 'origination'
  | 'fees'
  | 'dscr'
  | 'ltv'
  | 'deadline';

export interface OfferCellValueProps {
  fieldKey: CompareRowKey;
  offer: OfferRecord;
}

const CURRENCY_FMT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatCurrency(value: number): string {
  return CURRENCY_FMT.format(value);
}

const TNUM_SX = { fontVariantNumeric: 'tabular-nums' as const };

export function OfferCellValue({ fieldKey, offer }: OfferCellValueProps): ReactElement {
  const { t } = useI18n();

  switch (fieldKey) {
    case 'lender':
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {offer.lender}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
              ...TNUM_SX,
            }}
          >
            {offer.shortId}
          </Typography>
        </Box>
      );

    case 'amount':
      return (
        <Typography variant="body2" sx={{ fontWeight: 700, ...TNUM_SX }}>
          {formatCurrency(offer.amount)}
        </Typography>
      );

    case 'rate':
      return (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 700, ...TNUM_SX }}>
            {offer.rate.toFixed(2)}%
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('consumer.offers.fixed')}
          </Typography>
        </Box>
      );

    case 'term': {
      const years = Math.round(offer.termMonths / 12);
      return (
        <Typography variant="body2" sx={TNUM_SX}>
          {years} {t('consumer.offers.years')}
        </Typography>
      );
    }

    case 'monthly':
      return (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, ...TNUM_SX }}>
            {formatCurrency(offer.monthly)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('consumer.offers.perMonth')}
          </Typography>
        </Box>
      );

    case 'highlights': {
      const items = (offer.highlights ?? []).slice(0, 3);
      if (items.length === 0) {
        return (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        );
      }
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {items.map((h) => (
            <GlassChip key={h} label={h} size="small" color="success" variant="outlined" />
          ))}
        </Box>
      );
    }

    case 'cautions': {
      const items = offer.cautions ?? [];
      if (items.length === 0) {
        return (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        );
      }
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {items.map((c) => (
            <GlassChip key={c} label={c} size="small" color="warning" variant="outlined" />
          ))}
        </Box>
      );
    }

    case 'origination': {
      const pct = offer.originationPercent;
      const dollars = offer.originationDollars;
      if (pct === undefined && dollars === undefined) {
        return (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        );
      }
      return (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          {pct !== undefined && (
            <Typography variant="body2" sx={TNUM_SX}>
              {pct.toFixed(2)}%
            </Typography>
          )}
          {dollars !== undefined && (
            <Typography variant="caption" sx={{ color: 'text.secondary', ...TNUM_SX }}>
              ({formatCurrency(dollars)})
            </Typography>
          )}
        </Box>
      );
    }

    case 'fees':
      return (
        <Typography variant="body2" sx={{ fontWeight: 700, ...TNUM_SX }}>
          {formatCurrency(offer.fees)}
        </Typography>
      );

    case 'dscr':
      if (offer.dscr === undefined) {
        return (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        );
      }
      return (
        <Typography variant="body2" sx={TNUM_SX}>
          {offer.dscr.toFixed(2)}x
        </Typography>
      );

    case 'ltv':
      if (offer.ltvPercent === undefined) {
        return (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        );
      }
      return (
        <Typography variant="body2" sx={TNUM_SX}>
          {offer.ltvPercent.toFixed(0)}%
        </Typography>
      );

    case 'deadline':
      if (!offer.deadline) {
        return (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        );
      }
      return <GlassChip label={offer.deadline} size="small" color="error" variant="outlined" />;

    default:
      return <span />;
  }
}

OfferCellValue.displayName = 'OfferCellValue';
